import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import Loader from "@/components/Loader";
import { getEvents } from "@/utils/events";

type Props = {
  setProvince: (province: string) => void;
  loadingEvent: string | null;
  notFound: boolean;
};

const ProvincePicker = ({
  setProvince,
  loadingEvent = null,
  notFound = false,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [availableProvinces, setAvailableProvinces] = useState<string[] | null>(
    null,
  );

  const namesMap = {
    BritishColumbia: t("provinces.britishColumbia"),
    Ontario: t("provinces.ontario"),
    Quebec: t("provinces.quebec"),
  };

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const events = await getEvents();

      const now = new Date().getTime();
      const liveEvents = events.filter((event) => {
        const eventStartTime = new Date(event.start_time).getTime();
        const eventEndTime = new Date(event.end_time).getTime();
        return eventStartTime <= now && eventEndTime >= now;
      });

      const provinces = liveEvents
        .map((event) => event.province)
        .reduce((acc, province) => {
          if (acc.includes(province)) return acc;
          acc.push(province);
          return acc;
        }, [] as string[]);

      setAvailableProvinces(provinces);
      setIsLoading(false);
    } catch (_error) {
      setAvailableProvinces([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  return (
    <div className="w-full flex flex-col">
      <h3
        className={`text-${isLoading || availableProvinces?.length ? "xl" : "lg"} text-center font-bold my-[30px]`}
        dangerouslySetInnerHTML={{
          __html: isLoading
            ? t("signup.loading")
            : availableProvinces?.length
              ? t("signup.selectProvince")
              : t("signup.noProvinces"),
        }}
      />

      <div
        className={`flex flex-row justify-center items-center flex-wrap gap-y-[10px]${!availableProvinces?.length ? " my-30" : ""}`}
      >
        {isLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : availableProvinces?.length ? (
          availableProvinces.map((province) => (
            <div key={province}>
              <button
                className={`mx-2 bg-blue text-white text-sm min-w-[100px] min-h-[40px] rounded-[5px] font-semibold flex justify-center items-center ${
                  loadingEvent && loadingEvent !== province
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                disabled={!!(loadingEvent && loadingEvent !== province)}
                onClick={() => setProvince(province)}
              >
                {loadingEvent === province ? (
                  <Loader />
                ) : (
                  <span>{namesMap[province as keyof typeof namesMap]}</span>
                )}
              </button>
            </div>
          ))
        ) : (
          <div>
            <a
              className="mx-2 text-blue text-md min-w-[100px] min-h-[40px] rounded-[5px] font-semibold flex justify-center items-center"
              href="https://fordevroadtrip.com/"
            >
              https://fordevroadtrip.com/
            </a>
          </div>
        )}

        {notFound && (
          <div>
            <p
              className="text-center text-red-500 mt-5"
              dangerouslySetInnerHTML={{ __html: t("signup.eventNotFound") }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvincePicker;
